/**
 * CutsceneEngine - Async event runner for cutscene playback
 * Uses Promise-chain approach with blocking/non-blocking event support
 */

import { Application, Container } from "pixi.js";
import {
  CutsceneData,
  CutsceneEvent,
  CutsceneActor,
  Position,
  EasingType,
  SpawnPokemonEvent,
  MovePokemonEvent,
  AnimatePokemonEvent,
  RemovePokemonEvent,
  PlayVFXEvent,
  StopVFXEvent,
  CameraPanEvent,
  CameraZoomEvent,
  CameraShakeEvent,
  DialogEvent,
  HideDialogEvent,
  WaitEvent,
  FadeEvent,
} from "./types";

// ============================================================================
// Easing Functions
// ============================================================================

const easingFunctions: Record<EasingType, (t: number) => number> = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => --t * t * t + 1,
  easeInOutCubic: (t) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
};

function getEasing(type: EasingType = "linear"): (t: number) => number {
  return easingFunctions[type] || easingFunctions.linear;
}

// ============================================================================
// Engine Callbacks Interface
// ============================================================================

export interface CutsceneEngineCallbacks {
  /** Called when a Pokemon should be spawned */
  onSpawnPokemon: (event: SpawnPokemonEvent) => Promise<void>;
  /** Called when a Pokemon should move */
  onMovePokemon: (
    event: MovePokemonEvent,
    easing: (t: number) => number
  ) => Promise<void>;
  /** Called when a Pokemon animation should change */
  onAnimatePokemon: (event: AnimatePokemonEvent) => Promise<void>;
  /** Called when a Pokemon should be removed */
  onRemovePokemon: (event: RemovePokemonEvent) => Promise<void>;
  /** Called when VFX should play */
  onPlayVFX: (event: PlayVFXEvent) => Promise<void>;
  /** Called when VFX should stop */
  onStopVFX: (event: StopVFXEvent) => Promise<void>;
  /** Called when camera should pan */
  onCameraPan: (
    event: CameraPanEvent,
    easing: (t: number) => number
  ) => Promise<void>;
  /** Called when camera should zoom */
  onCameraZoom: (
    event: CameraZoomEvent,
    easing: (t: number) => number
  ) => Promise<void>;
  /** Called when camera should shake */
  onCameraShake: (event: CameraShakeEvent) => Promise<void>;
  /** Called when dialog should show */
  onShowDialog: (event: DialogEvent) => Promise<void>;
  /** Called when dialog should hide */
  onHideDialog: (event: HideDialogEvent) => Promise<void>;
  /** Called for screen fade */
  onFade: (event: FadeEvent) => Promise<void>;
  /** Called when event starts (for UI updates) */
  onEventStart?: (eventIndex: number, event: CutsceneEvent) => void;
  /** Called when event completes */
  onEventComplete?: (eventIndex: number, event: CutsceneEvent) => void;
  /** Called when cutscene completes */
  onComplete?: () => void;
  /** Called on error */
  onError?: (error: Error, event: CutsceneEvent) => void;
}

// ============================================================================
// CutsceneEngine Class
// ============================================================================

export class CutsceneEngine {
  private cutscene: CutsceneData;
  private callbacks: CutsceneEngineCallbacks;
  private isPlaying: boolean = false;
  private isPaused: boolean = false;
  private currentEventIndex: number = 0;
  private abortController: AbortController | null = null;

  constructor(cutscene: CutsceneData, callbacks: CutsceneEngineCallbacks) {
    this.cutscene = cutscene;
    this.callbacks = callbacks;
  }

  /**
   * Start playing the cutscene from the beginning
   */
  async play(): Promise<void> {
    if (this.isPlaying) return;

    this.isPlaying = true;
    this.isPaused = false;
    this.currentEventIndex = 0;
    this.abortController = new AbortController();

    try {
      await this.processEvents();
      this.callbacks.onComplete?.();
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        console.error("[CutsceneEngine] Playback error:", error);
      }
    } finally {
      this.isPlaying = false;
    }
  }

  /**
   * Stop the cutscene playback
   */
  stop(): void {
    this.abortController?.abort();
    this.isPlaying = false;
    this.isPaused = false;
    this.currentEventIndex = 0;
  }

  /**
   * Pause the cutscene (resume with resume())
   */
  pause(): void {
    this.isPaused = true;
  }

  /**
   * Resume a paused cutscene
   */
  resume(): void {
    this.isPaused = false;
  }

  /**
   * Get current playback state
   */
  getState(): {
    isPlaying: boolean;
    isPaused: boolean;
    currentEventIndex: number;
    totalEvents: number;
  } {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      currentEventIndex: this.currentEventIndex,
      totalEvents: this.cutscene.events.length,
    };
  }

  /**
   * Process all events in sequence
   */
  private async processEvents(): Promise<void> {
    const events = this.cutscene.events;

    while (this.currentEventIndex < events.length) {
      // Check for abort
      if (this.abortController?.signal.aborted) {
        throw new DOMException("Cutscene stopped", "AbortError");
      }

      // Wait while paused
      while (this.isPaused) {
        await this.delay(100);
        if (this.abortController?.signal.aborted) {
          throw new DOMException("Cutscene stopped", "AbortError");
        }
      }

      const event = events[this.currentEventIndex];
      this.callbacks.onEventStart?.(this.currentEventIndex, event);

      try {
        if (event.blocking) {
          // Blocking: wait for event to complete before next
          await this.executeEvent(event);
        } else {
          // Non-blocking: fire and forget (don't await)
          this.executeEvent(event).catch((err) => {
            this.callbacks.onError?.(err, event);
          });
        }

        this.callbacks.onEventComplete?.(this.currentEventIndex, event);
      } catch (error) {
        this.callbacks.onError?.(error as Error, event);
      }

      this.currentEventIndex++;
    }
  }

  /**
   * Execute a single event
   */
  private async executeEvent(event: CutsceneEvent): Promise<void> {
    switch (event.type) {
      case "spawn_pokemon":
        await this.callbacks.onSpawnPokemon(event);
        break;

      case "move_pokemon":
        await this.callbacks.onMovePokemon(event, getEasing(event.easing));
        break;

      case "animate_pokemon":
        await this.callbacks.onAnimatePokemon(event);
        break;

      case "remove_pokemon":
        await this.callbacks.onRemovePokemon(event);
        break;

      case "play_vfx":
        await this.callbacks.onPlayVFX(event);
        break;

      case "stop_vfx":
        await this.callbacks.onStopVFX(event);
        break;

      case "camera_pan":
        await this.callbacks.onCameraPan(event, getEasing(event.easing));
        break;

      case "camera_zoom":
        await this.callbacks.onCameraZoom(event, getEasing(event.easing));
        break;

      case "camera_shake":
        await this.callbacks.onCameraShake(event);
        break;

      case "dialog":
        await this.callbacks.onShowDialog(event);
        break;

      case "hide_dialog":
        await this.callbacks.onHideDialog(event);
        break;

      case "wait":
        await this.delay(event.duration);
        break;

      case "fade":
        await this.callbacks.onFade(event);
        break;

      default:
        console.warn(`[CutsceneEngine] Unknown event type:`, event);
    }
  }

  /**
   * Promise-based delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(resolve, ms);

      // Listen for abort to cancel the timeout
      this.abortController?.signal.addEventListener("abort", () => {
        clearTimeout(timeoutId);
        reject(new DOMException("Cutscene stopped", "AbortError"));
      });
    });
  }
}

// ============================================================================
// Animation Helpers
// ============================================================================

/**
 * Animate a value over time using requestAnimationFrame
 */
export function animateValue(
  from: number,
  to: number,
  duration: number,
  easing: (t: number) => number,
  onUpdate: (value: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      if (signal?.aborted) {
        reject(new DOMException("Animation cancelled", "AbortError"));
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);
      const value = from + (to - from) * easedProgress;

      onUpdate(value);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(animate);
  });
}

/**
 * Animate position over time
 */
export function animatePosition(
  from: Position,
  to: Position,
  duration: number,
  easing: (t: number) => number,
  onUpdate: (pos: Position) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      if (signal?.aborted) {
        reject(new DOMException("Animation cancelled", "AbortError"));
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);

      const x = from.x + (to.x - from.x) * easedProgress;
      const y = from.y + (to.y - from.y) * easedProgress;

      onUpdate({ x, y });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(animate);
  });
}

/**
 * Screen shake effect
 */
export function shakeScreen(
  stage: Container,
  intensity: number,
  duration: number,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const originalX = stage.x;
    const originalY = stage.y;
    const startTime = performance.now();

    // Cap intensity to reasonable values
    intensity = Math.max(0.1, Math.min(intensity, 5));
    duration = Math.max(50, Math.min(duration, 2000));

    const animate = (currentTime: number) => {
      if (signal?.aborted) {
        stage.x = originalX;
        stage.y = originalY;
        reject(new DOMException("Shake cancelled", "AbortError"));
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress < 1) {
        // Use sine waves with decreasing amplitude for natural shake
        const amplitude = intensity * (1 - progress) * 8;
        const frequency = 0.02;

        const offsetX = Math.sin(currentTime * frequency) * amplitude;
        const offsetY =
          Math.sin(currentTime * frequency * 1.5 + Math.PI / 4) * amplitude;

        stage.x = originalX + offsetX;
        stage.y = originalY + offsetY;

        requestAnimationFrame(animate);
      } else {
        // Reset to original position
        stage.x = originalX;
        stage.y = originalY;
        resolve();
      }
    };

    requestAnimationFrame(animate);
  });
}

/**
 * Typewriter effect for dialog text
 */
export function typewriterEffect(
  text: string,
  charsPerSecond: number,
  onUpdate: (displayedText: string) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (charsPerSecond <= 0) {
      onUpdate(text);
      resolve();
      return;
    }

    const msPerChar = 1000 / charsPerSecond;
    let currentIndex = 0;

    const typeNext = () => {
      if (signal?.aborted) {
        onUpdate(text); // Show full text on abort
        reject(new DOMException("Typewriter cancelled", "AbortError"));
        return;
      }

      currentIndex++;
      onUpdate(text.substring(0, currentIndex));

      if (currentIndex < text.length) {
        setTimeout(typeNext, msPerChar);
      } else {
        resolve();
      }
    };

    typeNext();
  });
}





























